import { useEffect, useState } from "react";
import { Plug, Gear, ShieldCheck, ArrowsClockwise, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChatGptBridgeCard } from "./ChatGptBridgeCard";

const ROUTING_POLICIES = ["balanced", "best_quality", "lowest_cost", "fastest", "lowest_latency", "highest_priority", "fallback_only", "manual"];

// Honest status vocabulary (SPRINT-108.6): only "verified" means a real health check succeeded.
const STATUS_VARIANT = {
  verified: "default", configured: "secondary", not_configured: "outline",
  invalid: "destructive", unavailable: "destructive", unconfigured: "outline",
};
const STATUS_LABEL = {
  verified: "Verified", configured: "Configured · Unverified", not_configured: "Not Configured",
  invalid: "Invalid", unavailable: "Unavailable", unconfigured: "Not Configured",
};

const NUM_FIELDS = [
  ["default_model", "Default Model", "text"],
  ["region", "Region", "text"],
  ["org_identifier", "Organization ID (optional)", "text"],
  ["project_identifier", "Project ID (optional)", "text"],
  ["monthly_budget", "Monthly Budget ($)", "number"],
  ["daily_budget", "Daily Budget ($)", "number"],
  ["monthly_quota", "Monthly Quota", "number"],
  ["daily_quota", "Daily Quota", "number"],
  ["rate_limit_per_min", "Rate Limit (per min)", "number"],
  ["cost_per_unit", "Cost per Unit ($)", "number"],
];

export default function ProvidersTab() {
  const { currentOrg } = useOrg();
  const [providers, setProviders] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [editing, setEditing] = useState(null); // provider being configured
  const [form, setForm] = useState({});
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);
  const [budget, setBudget] = useState(null);
  const [veo, setVeo] = useState(null);
  const [veoLimit, setVeoLimit] = useState("");
  const [savingVeo, setSavingVeo] = useState(false);

  const load = () => {
    api.get("/ai/kernel/providers").then((r) => setProviders(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/ai/kernel/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/ai/kernel/budget").then((r) => setBudget(r.data)).catch(() => {});
    api.get("/ai/kernel/veo/guardrail").then((r) => { setVeo(r.data); setVeoLimit(r.data.limit ?? ""); }).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const openConfig = (p) => {
    setEditing(p);
    setForm({
      enabled: p.enabled, default_model: p.default_model || "", region: p.region || "",
      org_identifier: p.org_identifier || "", project_identifier: p.project_identifier || "",
      monthly_budget: p.monthly_budget ?? "", daily_budget: p.daily_budget ?? "",
      monthly_quota: p.monthly_quota ?? "", daily_quota: p.daily_quota ?? "",
      rate_limit_per_min: p.rate_limit_per_min ?? "", cost_per_unit: p.cost_per_unit ?? "",
    });
    setCreds({});
  };

  const saveProvider = async () => {
    setSaving(true);
    try {
      const payload = { ...form, credentials: creds };
      // strip empty strings so we don't overwrite with blanks
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      ["monthly_budget", "daily_budget", "monthly_quota", "daily_quota", "rate_limit_per_min", "cost_per_unit"]
        .forEach((k) => { if (payload[k] !== undefined) payload[k] = Number(payload[k]); });
      await api.put(`/ai/kernel/providers/${editing.provider_id}`, payload);
      toast.success(`${editing.name} saved`);
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async (pid) => {
    try {
      const { data } = await api.post(`/ai/kernel/providers/${pid}/test`);
      toast[data.status === "error" ? "error" : "success"](`${pid}: ${data.status} — ${data.detail}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveVeo = async () => {
    setSavingVeo(true);
    try {
      const val = veoLimit === "" || veoLimit == null ? null : Number(veoLimit);
      const { data } = await api.put("/ai/kernel/veo/guardrail", { monthly_credit_limit: val });
      setVeo(data); setVeoLimit(data.limit ?? "");
      toast.success(val == null ? "Veo credit cap cleared" : `Veo monthly cap set to ${val} credits`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingVeo(false); }
  };

  const savePrefs = async () => {    try {
      const payload = { ...prefs };
      delete payload.org_id; delete payload.updated_at;
      ["monthly_budget", "daily_budget"].forEach((k) => { if (payload[k] === "" ) delete payload[k]; else if (payload[k] != null) payload[k] = Number(payload[k]); });
      await api.put("/ai/kernel/preferences", payload);
      toast.success("Organization AI preferences saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4 space-y-6" data-testid="ai-providers-tab">
      {budget && (
        <div className={`border rounded-sm px-4 py-3 text-xs flex items-center justify-between ${budget.allowed ? "border-border bg-card" : "border-destructive bg-destructive/10"}`} data-testid="ai-providers-budget">
          <span>
            <span className="font-medium">AI Budget</span> — Monthly {budget.monthly.limit ? `$${Number(budget.monthly.spend).toFixed(2)} / $${Number(budget.monthly.limit).toFixed(2)}` : "no limit"} · Daily {budget.daily.limit ? `$${Number(budget.daily.spend).toFixed(2)} / $${Number(budget.daily.limit).toFixed(2)}` : "no limit"} <span className="italic text-muted-foreground">(estimated)</span>
          </span>
          <Badge variant={budget.allowed ? "secondary" : "destructive"} className="rounded-sm text-[10px]">{budget.allowed ? (budget.warnings?.length ? "Warning" : "OK") : "Exceeded"}</Badge>
        </div>
      )}
      {/* Organization AI Preferences */}
      {prefs && (
        <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="ai-org-preferences">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg flex items-center gap-2"><ShieldCheck size={18} /> Organization AI Preferences</h3>
              <p className="text-xs text-muted-foreground">Enterprise defaults for this organization. Infrastructure only — modules adopt these in a future sprint.</p>
            </div>
            <Button onClick={savePrefs} className="rounded-sm" data-testid="save-preferences-button"><FloppyDisk size={16} className="mr-2" />Save</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[["default_text_model", "Default Text Model"], ["default_image_model", "Default Image Model"],
              ["default_video_model", "Default Video Model"], ["default_audio_model", "Default Audio Model"],
              ["default_embedding_model", "Default Embedding Model"], ["preferred_region", "Preferred Region"],
              ["monthly_budget", "Monthly Budget ($)"], ["daily_budget", "Daily Budget ($)"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Input value={prefs[k] ?? ""} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.value })}
                  className="rounded-sm" data-testid={`pref-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs">Default Routing Policy</Label>
              <Select value={prefs.default_routing_policy || "balanced"} onValueChange={(v) => setPrefs({ ...prefs, default_routing_policy: v })}>
                <SelectTrigger className="rounded-sm" data-testid="pref-routing-policy"><SelectValue /></SelectTrigger>
                <SelectContent>{ROUTING_POLICIES.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!prefs.fallback_enabled} onCheckedChange={(v) => setPrefs({ ...prefs, fallback_enabled: v })} data-testid="pref-fallback-switch" />
              <Label className="text-xs">Fallback Enabled</Label>
            </div>
          </div>
        </div>
      )}

      {/* Veo Cost Guardrail (SPRINT-108.9) — credit-based spend protection */}
      {veo && (
        <div className={`bg-card border rounded-sm p-6 space-y-4 ${veo.level === "blocked" ? "border-destructive" : veo.warnings?.length ? "border-amber-500/60" : "border-border"}`} data-testid="veo-guardrail">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg flex items-center gap-2"><ShieldCheck size={18} /> Veo Cost Guardrail</h3>
              <p className="text-xs text-muted-foreground">
                Optional monthly <span className="font-medium">credit</span> cap for real Veo video renders. Credits are usage units, not a currency charge. DEMO renders are free and never counted.
              </p>
            </div>
            <Badge
              variant={veo.level === "blocked" ? "destructive" : veo.warnings?.length ? "outline" : "secondary"}
              className="rounded-sm text-[10px]" data-testid="veo-guardrail-level">
              {veo.level === "blocked" ? "Blocked" : veo.level === "warn_85" ? "85% warning" : veo.level === "warn_70" ? "70% warning" : veo.configured ? "OK" : "No limit"}
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="rounded-sm border border-border bg-background/40 px-3 py-2" data-testid="veo-metric-estimate">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Est. / render</p>
              <p className="font-mono">{veo.estimate_per_render} cr</p>
            </div>
            <div className="rounded-sm border border-border bg-background/40 px-3 py-2" data-testid="veo-metric-used">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Used (this month)</p>
              <p className="font-mono">{veo.used} cr</p>
            </div>
            <div className="rounded-sm border border-border bg-background/40 px-3 py-2" data-testid="veo-metric-limit">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Monthly limit</p>
              <p className="font-mono">{veo.limit != null ? `${veo.limit} cr` : "—"}</p>
            </div>
            <div className="rounded-sm border border-border bg-background/40 px-3 py-2" data-testid="veo-metric-remaining">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Remaining</p>
              <p className="font-mono">{veo.remaining != null ? `${veo.remaining} cr` : "∞"}</p>
            </div>
          </div>

          {veo.configured && veo.pct != null && (
            <div className="space-y-1" data-testid="veo-utilization">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>Utilization</span><span>{Math.round((veo.pct || 0) * 100)}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${veo.level === "blocked" ? "bg-destructive" : veo.warnings?.length ? "bg-amber-500" : "bg-primary"}`}
                  style={{ width: `${Math.min(100, Math.round((veo.pct || 0) * 100))}%` }} />
              </div>
            </div>
          )}

          {veo.message && <p className="text-xs text-destructive" data-testid="veo-block-message">{veo.message}</p>}

          <div className="flex items-end gap-3 pt-2 border-t border-border">
            <div className="space-y-1.5">
              <Label className="text-xs">Monthly Veo credit limit <span className="text-muted-foreground">(blank = unlimited, admin only)</span></Label>
              <Input type="number" min="0" value={veoLimit} onChange={(e) => setVeoLimit(e.target.value)}
                placeholder="e.g. 500" className="rounded-sm w-48" data-testid="veo-limit-input" />
            </div>
            <Button onClick={saveVeo} disabled={savingVeo} className="rounded-sm" data-testid="veo-limit-save">
              <FloppyDisk size={16} className="mr-2" />{savingVeo ? "Saving…" : "Save cap"}
            </Button>
          </div>
        </div>
      )}

      {/* Provider grid */}
      <div>
        <h3 className="font-heading text-lg flex items-center gap-2 mb-3"><Plug size={18} /> AI Providers</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((p) => {
            if (p.provider_id === "chatgpt_browser") {
              return <ChatGptBridgeCard key={p.provider_id} provider={p} />;
            }
            return (
            <div key={p.provider_id} className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid={`ai-provider-card-${p.provider_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-[10px] font-mono text-muted-foreground uppercase">{p.kind}</p>
                </div>
                <Badge variant={STATUS_VARIANT[p.status] || "outline"} className="rounded-sm text-[10px] uppercase" data-testid={`ai-provider-status-${p.provider_id}`}>{STATUS_LABEL[p.status] || p.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {p.capabilities.map((c) => <Badge key={c} variant="outline" className="rounded-sm text-[9px]">{c}</Badge>)}
              </div>
              <div className="text-[11px] text-muted-foreground space-y-0.5">
                <div>Credentials: <span className={p.has_credentials ? "text-foreground" : ""}>{p.has_credentials ? "configured" : "—"}</span></div>
                <div>Health: {p.health} · Last check: {p.last_check ? new Date(p.last_check).toLocaleString() : "—"}</div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" className="rounded-sm flex-1" onClick={() => openConfig(p)} data-testid={`ai-provider-configure-${p.provider_id}`}><Gear size={14} className="mr-1" />Configure</Button>
                <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => testConnection(p.provider_id)} data-testid={`ai-provider-test-${p.provider_id}`}><ArrowsClockwise size={14} className="mr-1" />Test</Button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Configure dialog — fields rendered generically from the catalog (provider-agnostic) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="backdrop-blur-xl bg-background/90 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.name} — Configuration</DialogTitle>
            <DialogDescription>Organization-scoped. Credentials are encrypted in the Secrets Vault — never stored in plaintext.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="provider-enabled-switch" />
                <Label className="text-sm">Enabled for this organization</Label>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Credentials</p>
                {editing.credential_fields.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
                    <Input type={f.secret ? "password" : "text"} placeholder={f.secret && editing.has_credentials ? "•••••••• (leave blank to keep)" : ""}
                      value={creds[f.key] ?? ""} onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                      className="rounded-sm font-mono" data-testid={`provider-cred-${f.key}`} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {NUM_FIELDS.map(([k, label, type]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Input type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="rounded-sm" data-testid={`provider-field-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveProvider} disabled={saving} className="rounded-sm" data-testid="provider-save-button">
              <FloppyDisk size={16} className="mr-2" />{saving ? "Saving…" : "Save configuration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
